/**
 *
 *  Organizations/Show/AdditionalQuestions
 *
 */

import React, { useMemo, useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql, useMutation } from '@apollo/client'

// Components
import Modal from 'components/Modal'
import Checkbox from 'components/Checkbox'
import AdditionalQuestionForm from './Form'
import { FilterTopicSelector } from './TopicSelector'

// Utils
import { useCampaignTopicOptions } from 'hooks'
import { COLORS } from 'global-styles'

const GET_ORGANIZATION = gql`
	query organization($id: ID) {
		organization(id: $id) {
			id
			hide_user_campaign_additional_questions
			default_campaign_questions_required
			default_campaign_questions {
				id
				question
				topics
				options
				global
			}
		}
	}
`

const UPDATE_ORGANIZATION = gql`
	mutation updateOrganization(
		$id: ID!
		$default_campaign_questions_required: Boolean
		$hide_user_campaign_additional_questions: Boolean
	) {
		updateOrganization(
			input: {
				id: $id
				default_campaign_questions_required: $default_campaign_questions_required
				hide_user_campaign_additional_questions: $hide_user_campaign_additional_questions
			}
		) {
			organization {
				id
			}
		}
	}
`

const DELETE_ADDITIONAL_QUESTION = gql`
	mutation deleteCampaignAdditionalQuestion($id: ID!) {
		deleteCampaignAdditionalQuestion(input: { id: $id }) {
			campaign_additional_question {
				id
			}
		}
	}
`

const Table = styled.table`
	width: 100%;
	border: 1px solid ${COLORS.grey_e};

	th {
		font-size: 0.9rem;
		padding: 8px 12px;
		border: 1px solid ${COLORS.grey_e};
		border-left: 0;
		border-right: 0;
	}

	tbody {
		min-height: 400px;
		tr:hover {
			background-color: ${COLORS.lightGrey};

			.actions {
				visibility: visible;
			}
		}
	}

	td {
		padding: 8px 12px;
		border-bottom: 1px solid ${COLORS.grey_e};

		.question {
			display: block;
			width: 350px;
		}

		.responses,
		.topics {
			width: 300px;
			max-width: 350px;
			display: flex;
			align-items: center;

			.list {
				display: block;
				max-width: 270px;
				white-space: nowrap;
				overflow: hidden !important;
				text-overflow: ellipsis;
			}
		}

		.actions {
			min-width: 100px;
			display: flex;
			justify-content: end;
			visibility: hidden;

			.btn {
				padding: 2px 7px;
			}
		}
	}
`

function AdditionalQuestionRow({ question, topics_list, onEdit, onDelete, loading }) {
	const options_list = useMemo(() => {
		if (!question) return ''

		if (question.options?.length === 0) return 'Free Form'

		return question?.options.join(', ')
	}, [question])

	const topics_list_names = useMemo(() => {
		if (!question) return ''

		if (question.global) return 'For All Campaign Topics'
		if (question.topics.length === 0) return 'No Topic Selected'

		const topic_texts = question.topics
			.map((val) => topics_list.find((topic) => topic.value === val)?.text)
			.filter(Boolean)

		return topic_texts.join(', ')
	}, [question, topics_list])

	return (
		<tr>
			<td>
				<span className="question">{question.question}</span>
			</td>
			<td>
				<div className="responses">
					<span className="list">{options_list}</span>

					{options_list.length >= 40 && (
						<React.Fragment>
							<FontAwesomeIcon
								data-tip
								data-for={`${question.id}-responses`}
								icon="info-circle"
								className="text-dark ml-1"
							/>

							<ReactTooltip
								id={`${question.id}-responses`}
								className="unzoom"
								place="right"
								effect="float"
							>
								<p className="mb-2">Answer Options:</p>

								<ul className="mb-2 pl-4">
									{question.options.map((response, index) => (
										<li key={index}>{response}</li>
									))}
								</ul>
							</ReactTooltip>
						</React.Fragment>
					)}
				</div>
			</td>

			<td>
				<div className="topics">
					<span className="list">{topics_list_names}</span>

					{topics_list_names.length >= 30 && (
						<React.Fragment>
							<FontAwesomeIcon
								data-tip
								data-for={`${question.id}-topics`}
								icon="info-circle"
								className="text-dark float-right ml-1"
							/>

							<ReactTooltip id={`${question.id}-topics`} className="unzoom" place="right" effect="float">
								<p className="mb-2">Topics Selected:</p>

								<ul className="mb-2 pl-4">
									{question.topics.map((topic_key, index) => (
										<li key={index}>
											{topics_list.find((topic) => topic.value === topic_key)?.text}
										</li>
									))}
								</ul>
							</ReactTooltip>
						</React.Fragment>
					)}
				</div>
			</td>

			<td>
				<div className="actions">
					<button onClick={() => onEdit(question.id)} className="btn btn-warning btn-sm" disabled={loading}>
						Edit
					</button>

					<button onClick={onDelete} className="btn btn-danger btn-sm" disabled={loading}>
						Delete
					</button>
				</div>
			</td>
		</tr>
	)
}

function CampaignsAdditionalQuestions({ organization_id, handleClose }) {
	const [open_question_form, setOpenQuestionForm] = useState(null)
	const [open_topics_filters, setOpenTopicsFilter] = useState(false)
	const [topics_filter, setTopicsFilter] = useState([])

	const has_topics_active = topics_filter.length > 0

	const campaign_topic_options = useCampaignTopicOptions()

	const {
		data: {
			organization: {
				default_campaign_questions,
				default_campaign_questions_required,
				hide_user_campaign_additional_questions,
			},
		},
		loading: fetching_organization,
	} = useQuery(GET_ORGANIZATION, {
		variables: { id: organization_id },
		default_data: { organization: { default_campaign_questions: [] } },
		channels: [organization_id],
		skip: !organization_id,
		fetchPolicy: 'network-only',
	})

	const [updateOrganization, { loading: saving_organization }] = useMutation(UPDATE_ORGANIZATION)

	const [deleteQuestion, { loading: deleting_question }] = useMutation(DELETE_ADDITIONAL_QUESTION, {})

	const filtered_questions = useMemo(() => {
		if (!has_topics_active) return default_campaign_questions

		return default_campaign_questions.filter(
			(question) => question.topics.some((topic) => topics_filter.includes(topic)) || question.global,
		)
	}, [default_campaign_questions, topics_filter, has_topics_active])

	return (
		<Modal show modalDidClose={handleClose} style={{ width: 1200 }}>
			{(deleting_question || fetching_organization || saving_organization) && (
				<FontAwesomeIcon
					className="float-right text-primary"
					icon="spinner"
					spin
					style={{ fontSize: '25px' }}
				/>
			)}

			<h3 className="mb-3">Additional Questions by Campaign Topic</h3>

			<p>
				Create Additional Questions to be asked upon registration. These questions are visible on the Landing
				Page for web registrants. They are also asked by phone operators when handling phone registrations.
				<ul className="mt-1 pl-4">
					<li>Additional Questions automatically apply to all Campaigns associated with the Organization.</li>
					<li>Advisor-level Users can also create their own Additional Questions.</li>
					<li>Use the “Hide” checkbox to restrict advisor-level Users from creating their own questions.</li>
				</ul>
			</p>

			<hr />

			<div className="d-flex flex-column">
				<Checkbox
					label="Hide User-Level Campaign Additional Questions"
					checked={hide_user_campaign_additional_questions}
					onChange={() =>
						updateOrganization({
							variables: {
								id: organization_id,
								hide_user_campaign_additional_questions: !hide_user_campaign_additional_questions,
							},
						})
					}
					disabled={saving_organization}
				/>

				<Checkbox
					label="Set Default Campaign Additional Questions Required?"
					checked={default_campaign_questions_required}
					onChange={() =>
						updateOrganization({
							variables: {
								id: organization_id,
								default_campaign_questions_required: !default_campaign_questions_required,
							},
						})
					}
					disabled={saving_organization}
				/>
			</div>

			<div className="d-flex justify-content-between">
				<div>
					<button
						onClick={() => setOpenTopicsFilter(true)}
						className={`btn btn-${has_topics_active ? 'purple' : 'secondary'} my-3`}
					>
						<FontAwesomeIcon icon="filter" className="mr-1" size="sm" />

						{has_topics_active ? `Filtered by ${topics_filter.length} Topics` : 'Filter By Topic'}
					</button>

					{has_topics_active && (
						<FontAwesomeIcon
							icon="times-circle"
							className="text-danger ml-2 clickable"
							onClick={() => setTopicsFilter([])}
						/>
					)}
				</div>

				<button onClick={() => setOpenQuestionForm({})} className="btn btn-primary my-3">
					<FontAwesomeIcon icon="plus" className="mr-1" size="sm" />
					Create Question
				</button>
			</div>

			<Table>
				<thead>
					<tr>
						<th style={{ width: 350 }}>Question</th>
						<th>Responses Options</th>
						<th>Topics</th>
						<th aria-label="empty" />
					</tr>
				</thead>
				{filtered_questions?.length > 0 && (
					<tbody>
						{filtered_questions.map((question) => (
							<AdditionalQuestionRow
								question={question}
								topics_list={campaign_topic_options}
								onEdit={() => setOpenQuestionForm(question)}
								onDelete={() => deleteQuestion({ variables: { id: question.id } })}
								loading={deleting_question}
							/>
						))}
					</tbody>
				)}
			</Table>

			{filtered_questions?.length === 0 && (
				<div className="alert alert-info text-center mt-3">
					<b>There are no Additional Questions to show.</b>
				</div>
			)}

			<button onClick={handleClose} className="btn btn-primary mt-4">
				Close
			</button>

			{open_question_form && (
				<AdditionalQuestionForm
					additional_question={open_question_form}
					handleClose={() => setOpenQuestionForm(null)}
					organization_id={organization_id}
				/>
			)}

			{open_topics_filters && (
				<FilterTopicSelector
					topics={topics_filter}
					setTopics={(new_filters) => setTopicsFilter(new_filters)}
					handleClose={() => setOpenTopicsFilter(false)}
				/>
			)}
		</Modal>
	)
}

CampaignsAdditionalQuestions.propTypes = {
	organization_id: PropTypes.string,
	handleClose: PropTypes.func,
}
AdditionalQuestionRow.propTypes = {
	question: PropTypes.object,
	topics_list: PropTypes.array,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	loading: PropTypes.bool,
}

export default CampaignsAdditionalQuestions
